import { createContext, useContext, useEffect, useMemo, useState } from "react";
import tokensJson from "./tokens.json";

// The glass design system's token loader (session 18). A second, parallel
// token file to theme.ts and classical.ts, deliberately: this session only
// builds the shell, Sessions and Board, and the other surfaces keep reading
// theme.ts (or classical.ts, for the pre-rebuild Today) until the Part 1
// restyle pass reaches them. Everything here is derived from tokens.json,
// copied verbatim from mission-control/redesign/tokens.md — no colour is
// hand-typed in this file.

export const tokens = tokensJson;

export type ThemeMode = "system" | "light" | "dark";
type Resolved = "light" | "dark";

const STORAGE_KEY = "mc.themeMode";

function readStoredMode(): ThemeMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch { /* private mode, storage disabled — falls through to system */ }
  return "system";
}

function systemPrefersDark(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

type ThemeContextValue = {
  mode: ThemeMode;
  resolved: Resolved;
  colors: typeof tokens.light | typeof tokens.dark;
  setMode: (m: ThemeMode) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setSystemDark(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const setMode = (m: ThemeMode) => {
    setModeState(m);
    try { localStorage.setItem(STORAGE_KEY, m); } catch { /* private mode */ }
  };

  const resolved: Resolved = mode === "system" ? (systemDark ? "dark" : "light") : mode;
  const colors = resolved === "dark" ? tokens.dark : tokens.light;

  // `index.html`'s own `<body>` background is a static, light-only fallback
  // for the moment before this runs — it never updates itself afterwards.
  // On a viewport wider than the shell's 560px column (or before the shell
  // mounts at all, e.g. this provider wrapping something other than Shell
  // later), that fallback shows as a flat, un-themed margin around the app,
  // wrong in dark mode. Kept in sync here instead, the one place resolved
  // colour changes.
  useEffect(() => {
    document.documentElement.style.background = colors.ground;
    document.body.style.background = colors.ground;
    // The status bar and the installed app's launch colour follow the ground.
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", colors.ground);
  }, [colors.ground]);

  const value = useMemo(() => ({ mode, resolved, colors, setMode }), [mode, resolved, colors]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
}
